/**
 * ChatPage — /chat/:chatId
 *
 * WhatsApp-style 1-on-1 thread. Socket.io subscribes to `chat:{id}` so
 * new messages from the other side render instantly. Free suppliers hitting
 * the 3-active-chat cap get a locked input with the exact upgrade copy.
 *
 * Phase C: supplier can drop Quote messages (price + hours). Customer taps
 * Accept → collision-checked booking. Decline → quote goes inert.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { io } from 'socket.io-client';
import { toast } from 'sonner';
import { ArrowLeft, PaperPlaneRight, Lock, CheckCircle, XCircle, Calendar, Lightning, CurrencyGbp, Warning, Clock, MapPin, EnvelopeSimple, Phone, CaretDown, CaretUp, ShieldCheck, FileText } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QUICK_REPLIES = [
  "Yes, I'm available",
  "Here's a quote",
  "Can you share more details?",
];

const ChatPage = () => {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [locked, setLocked] = useState(false); // supplier free-tier cap hit
  const [bookingBusy, setBookingBusy] = useState(false);
  const [bookingLockedModal, setBookingLockedModal] = useState(false);
  const [quoteFormOpen, setQuoteFormOpen] = useState(false);
  const [quotePrice, setQuotePrice] = useState('');
  const [quoteHours, setQuoteHours] = useState('');
  const [quoteBusy, setQuoteBusy] = useState(false);
  const [justConfirmed, setJustConfirmed] = useState(false);
  const [certSendOpen, setCertSendOpen] = useState(false);
  const scrollerRef = useRef(null);
  const socketRef = useRef(null);

  // First-tap chat pre-fill: when the guest came in via the
  // SupplierBrowseLayer continuation flow, that layer stashed a
  // friendly intro keyed by supplier_id. Seed it into the composer
  // exactly once when chat loads, then delete the key. 10-minute
  // TTL guards against ancient stash entries leaking into unrelated
  // chats opened much later.
  useEffect(() => {
    if (!chat?.supplier_id) return;
    if (input) return; // user already typing — don't clobber
    if (messages && messages.length > 0) return; // not a brand-new chat
    try {
      const key = `chat_intro_${chat.supplier_id}`;
      const raw = sessionStorage.getItem(key);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed?.text || (Date.now() - (parsed.ts || 0)) > 10 * 60 * 1000) {
        sessionStorage.removeItem(key);
        return;
      }
      setInput(parsed.text);
      sessionStorage.removeItem(key);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat?.supplier_id, messages?.length]);

  const scrollToBottom = useCallback(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  // Initial fetch
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/chats/${chatId}/messages`)
      .then((r) => {
        if (cancelled) return;
        setChat(r.data.chat);
        setMessages(r.data.messages || []);
        setBookings(r.data.bookings || []);
        setTimeout(scrollToBottom, 50);
      })
      .catch(() => {
        if (!cancelled) toast.error('Chat not available');
      });
    return () => { cancelled = true; };
  }, [chatId, scrollToBottom]);

  // Socket.io room subscribe — Phase D events: message_new, quote_sent, booking_confirmed
  useEffect(() => {
    const socket = io(BACKEND_URL, { path: '/api/socket.io', transports: ['websocket', 'polling'] });
    socketRef.current = socket;
    socket.on('connect', () => socket.emit('chat_join', { chat_id: chatId }));

    const upsertMessage = (m) => {
      if (m.chat_id !== chatId) return;
      setMessages((prev) => {
        const idx = prev.findIndex((x) => x.id === m.id);
        if (idx >= 0) { const next = prev.slice(); next[idx] = m; return next; }
        return [...prev, m];
      });
      setTimeout(scrollToBottom, 30);
    };
    const upsertBooking = (b) => {
      if (b.chat_id !== chatId) return;
      setBookings((prev) => {
        const idx = prev.findIndex((x) => x.id === b.id);
        if (idx >= 0) { const next = prev.slice(); next[idx] = b; return next; }
        return [...prev, b];
      });
      setChat((prev) => (prev ? { ...prev, booking_status: b.status } : prev));
      setTimeout(scrollToBottom, 30);
    };

    socket.on('message_new', upsertMessage);
    socket.on('quote_sent', upsertMessage);
    socket.on('certificates_sent', upsertMessage);
    socket.on('booking_confirmed', upsertBooking);
    // Fallback for the legacy instant-booking flow (pending/declined state changes)
    socket.on('chat_booking', upsertBooking);

    return () => {
      try { socket.emit('chat_leave', { chat_id: chatId }); } catch { /* noop */ }
      socket.disconnect();
    };
  }, [chatId, scrollToBottom]);

  const send = async (override) => {
    const text = (override ?? input).trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/messages`, { chat_id: chatId, message: text });
      setMessages((prev) => (prev.some((x) => x.id === data.message.id) ? prev : [...prev, data.message]));
      if (override === undefined) setInput('');
      setTimeout(scrollToBottom, 30);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 403 && detail?.error === 'chat_limit_reached') {
        setLocked(true);
      } else {
        toast.error(detail?.message || detail || 'Failed to send');
      }
    } finally {
      setSending(false);
    }
  };

  const sendQuote = async () => {
    const price = parseFloat(quotePrice);
    const hours = parseFloat(quoteHours);
    if (!(price > 0) || !(hours > 0)) {
      toast.error('Enter a price and duration');
      return;
    }
    if (quoteBusy) return;
    setQuoteBusy(true);
    // Stable client-side id so double-taps / retries don't duplicate.
    const clientQuoteId = `cq_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    try {
      const { data } = await axios.post(`${API}/bookings/quote`, {
        chat_id: chatId, price, duration_hours: hours, client_quote_id: clientQuoteId,
      });
      setMessages((prev) => (prev.some((x) => x.id === data.message.id) ? prev : [...prev, data.message]));
      setQuoteFormOpen(false);
      setQuotePrice('');
      setQuoteHours('');
      toast.success('Quote sent');
      setTimeout(scrollToBottom, 30);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 403 && detail?.error === 'chat_limit_reached') {
        setLocked(true);
      } else {
        toast.error(detail?.message || detail || 'Could not send quote');
      }
    } finally {
      setQuoteBusy(false);
    }
  };

  const respondQuote = async (msgId, action) => {
    try {
      const path = action === 'accept' ? 'accept-quote' : 'decline-quote';
      const { data } = await axios.post(`${API}/bookings/${path}`, {
        chat_id: chatId, message_id: msgId,
      });
      setMessages((prev) => prev.map((m) => (m.id === msgId ? data.message : m)));
      if (action === 'accept' && data.booking) {
        setBookings((prev) => [...prev, data.booking]);
        setChat((prev) => ({ ...prev, booking_status: 'confirmed' }));
        setJustConfirmed(true);
        toast.success('Booking confirmed 🎉');
        setTimeout(() => setJustConfirmed(false), 8000);
      } else if (action === 'decline') {
        toast('Quote declined');
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.error === 'calendar_collision') {
        toast.error(detail.message || 'This date is no longer available');
      } else {
        toast.error(detail?.message || detail || 'Could not update quote');
      }
    }
  };

  const sendBooking = async () => {
    if (bookingBusy) return;
    setBookingBusy(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, { chat_id: chatId });
      setBookings((prev) => [...prev, data.booking]);
      setChat((prev) => ({ ...prev, booking_status: 'pending' }));
      toast.success('Booking request sent');
      setTimeout(scrollToBottom, 30);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 403 && detail?.error === 'upgrade_required') {
        setBookingLockedModal(true);
      } else {
        toast.error(detail?.message || detail || 'Could not send booking');
      }
    } finally {
      setBookingBusy(false);
    }
  };

  const respondBooking = async (bookingId, action) => {
    try {
      const { data } = await axios.post(`${API}/bookings/${bookingId}/${action}`);
      setBookings((prev) => prev.map((b) => (b.id === bookingId ? data.booking : b)));
      setChat((prev) => ({ ...prev, booking_status: data.booking.status }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update booking');
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!chat) {
    return (
      <div className="min-h-screen bg-[#0F1014] text-white flex items-center justify-center" data-testid="chat-loading">
        <p className="text-white/40 text-sm">Loading chat…</p>
      </div>
    );
  }

  const isSupplier = chat.my_role === 'supplier';
  const pendingBooking = bookings.find((b) => b.status === 'pending');
  const hasPendingQuote = messages.some(
    (m) => m.type === 'quote' && m.quote?.status === 'pending'
  );
  const chatConfirmed = chat.booking_status === 'confirmed';
  const showUnavailWarning = isSupplier && chat.unavailable_on_event_date && !chatConfirmed;

  // Merge messages + bookings and sort by created_at for a single rendered timeline
  const timeline = [
    ...messages.map((m) => ({
      ...m,
      _kind: m.type === 'quote' ? 'quote' : m.type === 'certificates_sent' ? 'certs' : 'msg',
    })),
    ...bookings.filter((b) => b.source !== 'quote').map((b) => ({ ...b, _kind: 'booking' })),
  ].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));

  return (
    <div className="min-h-screen bg-[#0F1014] text-white flex flex-col" data-testid="chat-page">
      {/* Header */}
      <div className="sticky top-0 z-10 border-b border-white/5 bg-[#0F1014]/95 backdrop-blur px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-white/60 hover:text-white" data-testid="chat-back-btn">
          <ArrowLeft size={18} weight="bold" />
        </button>
        <div className="flex-1 min-w-0">
          {isSupplier && chat.event_context ? (
            <>
              <p className="font-bold text-sm truncate flex items-center gap-1.5" data-testid="chat-header-name">
                {chat.customer_name || 'Customer'} {chat.event_context.event_type_label}
                <span>{chat.event_context.event_type_emoji}</span>
              </p>
              <p className="text-white/40 text-[10px] truncate">
                {chat.event_context.event_date ? `📅 ${formatBookingDate(chat.event_context.event_date)}` : ''}
                {chat.event_context.event_postcode || chat.event_context.event_location
                  ? ` · 📍 ${chat.event_context.event_location || chat.event_context.event_postcode}`
                  : ''}
                {chat.booking_status === 'confirmed' && ' · Booked'}
              </p>
            </>
          ) : (
            <>
              <p className="font-bold text-sm truncate flex items-center gap-1.5" data-testid="chat-header-name">
                {chat.supplier_name}
                {!isSupplier && chat.supplier_verified && (
                  <span
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-black bg-emerald-400/15 border border-emerald-400/40 text-emerald-200"
                    data-testid="chat-verified-pill"
                    title="PAT + PLI on file and in-date"
                  >
                    <CheckCircle size={9} weight="fill" /> Verified
                  </span>
                )}
              </p>
              <p className="text-white/40 text-[10px]">
                {isSupplier ? 'Customer enquiry' : 'Supplier'}
                {chat.booking_status === 'confirmed' && ' · Booked'}
              </p>
            </>
          )}
        </div>
      </div>

      {/* Not-verified nudge — supplier-only, helps them self-serve the missing certs */}
      {isSupplier && chat.supplier_verified === false && (
        <button
          onClick={() => navigate('/dashboard/supplier/compliance')}
          className="mx-4 mt-3 rounded-xl border border-amber-400/30 bg-amber-400/10 text-amber-100 px-3 py-2 flex items-start gap-2 w-auto text-left"
          data-testid="supplier-not-verified-nudge"
        >
          <ShieldCheck size={14} weight="fill" className="mt-0.5 shrink-0" />
          <p className="text-xs leading-snug">
            <span className="font-bold">You're not fully verified.</span>
            <span className="text-amber-200/80"> Upload PAT + PLI to increase booking chances →</span>
          </p>
        </button>
      )}

      {/* Lead brief (supplier-only) — the qualification the customer submitted.
          Helps the supplier decide whether to respond before reading chat. */}
      {isSupplier && chat.event_context && (
        <LeadBrief
          ctx={chat.event_context}
          contact={chat.customer_contact}
          customerName={chat.customer_name}
        />
      )}

      {/* Unavailability warning (supplier-only, event_date blocked/booked/external) */}
      {showUnavailWarning && (
        <div
          className="mx-4 mt-3 rounded-xl border border-amber-400/30 bg-amber-400/10 text-amber-100 px-3 py-2 flex items-start gap-2"
          data-testid="chat-unavailable-warning"
        >
          <Warning size={16} weight="fill" className="mt-0.5 shrink-0" />
          <p className="text-xs leading-snug">
            You&rsquo;re marked unavailable on{' '}
            <span className="font-bold">{formatBookingDate(chat.event_context?.event_date)}</span>.
            Update your calendar before quoting.
          </p>
        </div>
      )}

      {/* Just-confirmed celebration banner (customer side, auto-hides after 8s) */}
      {justConfirmed && !isSupplier && (
        <div
          className="mx-4 mt-3 rounded-xl border border-emerald-400/30 bg-emerald-400/10 text-emerald-100 px-3 py-3 text-center"
          data-testid="chat-just-confirmed-banner"
        >
          <p className="text-sm font-black">Booking confirmed 🎉</p>
          <p className="text-[11px] text-emerald-200/80 mt-0.5">This event is now locked in.</p>
        </div>
      )}

      {/* Timeline */}
      <div ref={scrollerRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-2" data-testid="chat-messages">
        {/* Micro-feedback: show ranking hint when supplier opens a new unread enquiry */}
        {isSupplier && messages.some((m) => m.sender_role === 'customer') && messages.filter((m) => m.sender_role === 'supplier').length === 0 && (
          <div className="flex justify-center mb-2" data-testid="chat-ranking-hint">
            <p className="text-[10px] font-bold text-[#C29D52]/70 text-center px-3 py-1.5 rounded-full bg-[#B89762]/30/5 border border-[#B89762]/40/20">
              ⚡ You appeared in search because you reply quickly
            </p>
          </div>
        )}
        {timeline.length === 0 && (
          <p className="text-center text-white/30 text-xs py-8" data-testid="chat-empty">
            {isSupplier
              ? 'This is the start of the conversation.'
              : 'Say hi — suppliers reply fastest to specific enquiries.'}
          </p>
        )}
        {timeline.map((item) => {
          if (item._kind === 'booking') {
            return (
              <BookingCard
                key={`b-${item.id}`}
                booking={item}
                isSupplier={isSupplier}
                onAccept={() => respondBooking(item.id, 'accept')}
                onDecline={() => respondBooking(item.id, 'decline')}
                supplierName={chat.supplier_name}
                eventContext={chat.event_context}
              />
            );
          }
          if (item._kind === 'quote') {
            return (
              <QuoteCard
                key={`q-${item.id}`}
                message={item}
                isSupplier={isSupplier}
                onAccept={() => respondQuote(item.id, 'accept')}
                onDecline={() => respondQuote(item.id, 'decline')}
                eventContext={chat.event_context}
              />
            );
          }
          if (item._kind === 'certs') {
            return (
              <CertificatesSentCard
                key={`c-${item.id}`}
                message={item}
                isSupplier={isSupplier}
              />
            );
          }
          const mine = item.sender_role === chat.my_role;
          return (
            <div key={item.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${item.id}`}>
              <div
                className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${mine ? 'text-white' : 'bg-white/10 text-white'}`}
                style={mine ? { background: 'linear-gradient(135deg, #B89762, #C29D52)' } : undefined}
              >
                {item.message}
              </div>
            </div>
          );
        })}
      </div>

      {/* Supplier quote form (Phase C) */}
      {isSupplier && quoteFormOpen && (
        <div className="border-t border-white/5 px-3 pt-3" data-testid="quote-form">
          <div className="rounded-xl border border-[#B89762]/40/20 bg-[#B89762]/30/5 p-3">
            <p className="text-[11px] uppercase tracking-wider font-black text-[#C29D52]/85 mb-2">Send a quote</p>
            <div className="flex gap-2 mb-2">
              <div className="flex-1 flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
                <CurrencyGbp size={14} className="text-white/40" />
                <input
                  type="number"
                  min="1"
                  step="1"
                  placeholder="Price"
                  value={quotePrice}
                  onChange={(e) => setQuotePrice(e.target.value)}
                  className="flex-1 bg-transparent text-sm text-white placeholder-white/30 focus:outline-none"
                  data-testid="quote-price-input"
                />
              </div>
              <div className="flex-1 flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
                <input
                  type="number"
                  min="0.5"
                  step="0.5"
                  placeholder="Hours"
                  value={quoteHours}
                  onChange={(e) => setQuoteHours(e.target.value)}
                  className="flex-1 bg-transparent text-sm text-white placeholder-white/30 focus:outline-none"
                  data-testid="quote-hours-input"
                />
                <span className="text-white/40 text-xs">hrs</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={sendQuote}
                disabled={quoteBusy}
                className="flex-1 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
                data-testid="quote-submit-btn"
              >
                {quoteBusy ? 'Sending…' : 'Send quote'}
              </button>
              <button
                onClick={() => { setQuoteFormOpen(false); setQuotePrice(''); setQuoteHours(''); }}
                className="px-3 py-2 rounded-lg text-xs font-bold bg-white/5 border border-white/10 hover:bg-white/10"
                data-testid="quote-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Supplier-only quote + booking CTAs (above input) */}
      {isSupplier && !chatConfirmed && !pendingBooking && !hasPendingQuote && !quoteFormOpen && !locked && (
        <div className="border-t border-white/5 px-3 pt-3 flex gap-2">
          <button
            onClick={() => setQuoteFormOpen(true)}
            className="flex-1 py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 text-white"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
            data-testid="send-quote-btn"
          >
            <CurrencyGbp size={14} weight="fill" />
            Send Quote
          </button>
          <button
            onClick={() => setCertSendOpen(true)}
            className="flex-1 py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 border border-[#B89762]/40/20 bg-[#B89762]/30/5 hover:bg-[#B89762]/30/10 text-white"
            data-testid="send-certificates-btn"
          >
            <FileText size={14} weight="fill" />
            Send Certificates
          </button>
          <button
            onClick={sendBooking}
            disabled={bookingBusy}
            className="px-4 py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 border border-white/10 bg-white/[0.04] hover:bg-white/[0.07] disabled:opacity-60"
            data-testid="send-booking-btn"
          >
            <Calendar size={14} weight="fill" />
            {bookingBusy ? '…' : 'Request'}
          </button>
        </div>
      )}

      {/* Input / locked state */}
      {locked ? (
        <div className="border-t border-white/5 p-4" data-testid="chat-locked">
          <div className="rounded-xl p-4 bg-white/[0.03] border border-white/10 text-center">
            <Lock size={18} weight="fill" className="mx-auto mb-2 text-[#C29D52]" />
            <p className="font-bold text-sm mb-1">Don&rsquo;t miss this booking</p>
            <p className="text-white/60 text-xs mb-3">
              You&rsquo;ve reached your response limit. Upgrade to Studio Pass to reply to this customer and secure the date.
            </p>
            <button
              onClick={() => navigate('/studio-pass')}
              className="w-full py-2.5 rounded-lg text-xs font-bold text-white"
              style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
              data-testid="upgrade-studio-pass-btn"
            >
              Upgrade to Studio Pass
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Quick replies (3 dumb chips) — hidden when confirmed, quote form open, or free-tier locked */}
          {!chatConfirmed && !quoteFormOpen && !hasPendingQuote && (
            <div className="border-t border-white/5 px-3 pt-2 flex gap-1.5 overflow-x-auto" data-testid="chat-quick-replies">
              {QUICK_REPLIES.map((q) => (
                <button
                  key={q}
                  onClick={() => send(q)}
                  disabled={sending}
                  className="shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold bg-white/[0.04] hover:bg-white/[0.08] text-white/70 hover:text-white border border-white/10 disabled:opacity-60"
                  data-testid={`chat-quick-reply-${q.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`}
                >
                  {q}
                </button>
              ))}
            </div>
          )}
          <div className="sticky bottom-0 border-t border-white/5 bg-[#0F1014]/95 backdrop-blur p-3 flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Type a message…"
              className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#B89762]/40/40"
              autoFocus
              data-testid="chat-input"
            />
            <button
              onClick={() => send()}
              disabled={!input.trim() || sending}
              className="w-10 h-10 rounded-full flex items-center justify-center disabled:opacity-40"
              style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
              data-testid="chat-send-btn"
            >
              <PaperPlaneRight size={16} weight="fill" />
            </button>
          </div>
        </>
      )}

      {/* Cert send modal — chat-path flow */}
      {certSendOpen && (
        <CertSendChatModal
          chatId={chatId}
          recipientEmail={chat.customer_contact?.email}
          recipientName={chat.customer_name}
          onClose={() => setCertSendOpen(false)}
          onSent={(msg) => {
            setCertSendOpen(false);
            if (msg) {
              setMessages((prev) => (prev.some((x) => x.id === msg.id) ? prev : [...prev, msg]));
              setTimeout(scrollToBottom, 30);
            }
          }}
          onUpgrade={() => { setCertSendOpen(false); navigate('/studio-pass'); }}
          onGoCompliance={() => { setCertSendOpen(false); navigate('/dashboard/supplier/compliance'); }}
        />
      )}

      {/* Booking-lock modal (supplier free tier tries Send Booking) */}
      {bookingLockedModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6"
          onClick={() => setBookingLockedModal(false)}
          data-testid="booking-locked-modal"
        >
          <div className="max-w-sm w-full rounded-2xl p-6 bg-[#15161B] border border-white/10" onClick={(e) => e.stopPropagation()}>
            <Lock size={24} weight="fill" className="mb-3 text-[#C29D52]" />
            <h3 className="font-black text-lg mb-1">Upgrade to confirm bookings</h3>
            <p className="text-white/60 text-sm mb-5">
              Send Booking Requests are a Studio Pass feature. Upgrade to lock in this date and secure more bookings this season.
            </p>
            <button
              onClick={() => navigate('/studio-pass')}
              className="w-full py-3 rounded-xl text-sm font-bold text-white"
              style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
              data-testid="booking-locked-upgrade-btn"
            >
              Upgrade to Studio Pass
            </button>
            <button
              onClick={() => setBookingLockedModal(false)}
              className="w-full py-2 mt-2 text-xs text-white/40 hover:text-white/80"
            >
              Not now
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const SERVICE_META = {
  dj: { label: 'DJ / Band', emoji: '🎧' }, photographer: { label: 'Photographer', emoji: '📷' },
  videographer: { label: 'Videographer', emoji: '🎥' }, photobooth: { label: 'Photo Booth', emoji: '📸' },
  caterer: { label: 'Caterer', emoji: '🍽️' }, florist: { label: 'Florist', emoji: '🌼' },
  venue: { label: 'Venue', emoji: '🏛️' }, cake_maker: { label: 'Cake Maker', emoji: '🎂' },
  transport: { label: 'Transport', emoji: '🚗' }, decorator: { label: 'Decorator', emoji: '🎨' },
  wedding_planner: { label: 'Wedding Planner', emoji: '✨' }, hair_makeup: { label: 'Hair & Makeup', emoji: '💅' },
};

const LeadBrief = ({ ctx, contact, customerName }) => {
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();
  const hasAnything = ctx.start_time || ctx.end_time || (ctx.services_required && ctx.services_required.length)
    || ctx.budget_min != null || ctx.budget_max != null || contact;
  if (!hasAnything) return null;

  const budgetLabel = ctx.budget_min != null && ctx.budget_max != null
    ? `£${ctx.budget_min.toLocaleString('en-GB')} – £${ctx.budget_max.toLocaleString('en-GB')}`
    : null;
  const timeLabel = ctx.start_time || ctx.end_time
    ? `${ctx.start_time || '—'} to ${ctx.end_time || '—'}`
    : null;

  return (
    <div className="mx-4 mt-3 rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden" data-testid="lead-brief">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-white/[0.02]"
        data-testid="lead-brief-toggle"
      >
        <div className="flex items-center gap-2">
          <p className="text-[10px] font-black uppercase tracking-wider text-[#C29D52]/85">Lead details</p>
          {customerName && <p className="text-xs text-white/60">· {customerName}</p>}
        </div>
        {open ? <CaretUp size={14} className="text-white/40" /> : <CaretDown size={14} className="text-white/40" />}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2.5">
          {(ctx.event_date || ctx.event_postcode || ctx.event_location) && (
            <BriefRow icon={<MapPin size={12} weight="fill" />}>
              <span className="text-white/85">{ctx.event_date ? formatBookingDate(ctx.event_date) : ''}</span>
              {(ctx.event_postcode || ctx.event_location) && (
                <span className="text-white/60">
                  {' · '}{ctx.event_location || ctx.event_postcode}
                </span>
              )}
            </BriefRow>
          )}
          {timeLabel && (
            <BriefRow icon={<Clock size={12} weight="fill" />}>
              <span className="text-white/85">{timeLabel}</span>
            </BriefRow>
          )}
          {ctx.services_required && ctx.services_required.length > 0 && (
            <BriefRow icon={<Lightning size={12} weight="fill" />}>
              <div className="flex flex-wrap gap-1">
                {ctx.services_required.map((s) => (
                  <span
                    key={s}
                    className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#B89762]/30/10 text-[#F4E9D2] border border-[#B89762]/40/20"
                  >
                    {(SERVICE_META[s] || {}).emoji} {(SERVICE_META[s] || { label: s }).label}
                  </span>
                ))}
              </div>
            </BriefRow>
          )}
          {budgetLabel && (
            <BriefRow icon={<CurrencyGbp size={12} weight="fill" />}>
              <span className="text-white/85 font-bold">{budgetLabel}</span>
              <span className="text-white/40 text-[11px]">{' '}budget</span>
            </BriefRow>
          )}
          {ctx.guest_count && (
            <BriefRow icon={<span className="text-[11px]">👥</span>}>
              <span className="text-white/85">{ctx.guest_count} guests</span>
            </BriefRow>
          )}

          {/* Contact unlock block — Studio Pass gates email + phone */}
          {contact && contact.unlocked ? (
            <div className="mt-2 pt-2 border-t border-white/5 space-y-1.5" data-testid="contact-unlocked">
              {contact.email && (
                <BriefRow icon={<EnvelopeSimple size={12} weight="fill" />}>
                  <a href={`mailto:${contact.email}`} className="text-[#C29D52]/85 hover:text-[#F4E9D2] font-bold truncate">
                    {contact.email}
                  </a>
                </BriefRow>
              )}
              {contact.phone && (
                <BriefRow icon={<Phone size={12} weight="fill" />}>
                  <a href={`tel:${contact.phone}`} className="text-[#C29D52]/85 hover:text-[#F4E9D2] font-bold">
                    {contact.phone}
                  </a>
                </BriefRow>
              )}
            </div>
          ) : contact && !contact.unlocked ? (
            <button
              type="button"
              onClick={() => navigate('/studio-pass')}
              className="mt-2 w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border border-[#B89762]/40/20 bg-[#B89762]/30/5 hover:bg-[#B89762]/30/10"
              data-testid="contact-locked"
            >
              <Lock size={14} weight="fill" className="text-[#C29D52] shrink-0" />
              <span className="flex-1 text-left text-[11px]">
                <span className="text-white font-bold block leading-tight">Upgrade to view contact details</span>
                <span className="text-white/50 block mt-0.5">Email + phone unlocked with Studio Pass</span>
              </span>
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
};

const BriefRow = ({ icon, children }) => (
  <div className="flex items-start gap-2 text-xs">
    <div className="text-[#C29D52]/70 mt-0.5 shrink-0 w-3 flex items-center justify-center">{icon}</div>
    <div className="flex-1 min-w-0">{children}</div>
  </div>
);

const CertificatesSentCard = ({ message, isSupplier }) => {
  const certs = message.certificates || [];
  return (
    <div className="flex justify-center" data-testid={`cert-sent-${message.id}`}>
      <div className="rounded-2xl px-4 py-3 border border-[#B89762]/40/25 bg-[#B89762]/30/5 w-full max-w-sm text-white">
        <div className="flex items-center gap-2 mb-2 text-[#C29D52]/85">
          <FileText size={14} weight="fill" />
          <p className="font-black text-[11px] uppercase tracking-wider">
            {isSupplier ? 'Certificates sent' : 'Certificates received'}
          </p>
        </div>
        <ul className="space-y-1 mb-2">
          {certs.map((c) => (
            <li key={c.type} className="flex items-center gap-2 text-sm">
              <CheckCircle size={12} weight="fill" className="text-emerald-300 shrink-0" />
              <span className="font-bold">
                {c.type === 'pat' ? 'PAT Certificate' : c.type === 'pli' ? 'Public Liability Insurance' : c.type.toUpperCase()}
              </span>
              {c.certificate_number && (
                <span className="text-white/40 text-[11px]">· {c.certificate_number}</span>
              )}
            </li>
          ))}
        </ul>
        <p className="text-white/40 text-[10px]">
          {isSupplier ? 'Emailed to customer' : 'Check your inbox for attachments'} · {formatRelative(message.created_at)}
        </p>
      </div>
    </div>
  );
};

const CertSendChatModal = ({ chatId, recipientEmail, recipientName, onClose, onSent, onUpgrade, onGoCompliance }) => {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPro, setIsPro] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [name, setName] = useState(recipientName || '');
  const [emailAddr, setEmailAddr] = useState(recipientEmail || '');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/suppliers/me/certificates`).catch(() => ({ data: { certificates: [] } })),
      axios.get(`${API}/suppliers/me/chat-quota`).catch(() => ({ data: { is_pro: false } })),
    ]).then(([a, b]) => {
      const active = (a.data.certificates || []).filter((c) => c.status !== 'expired');
      setCerts(active);
      setSelectedIds(active.map((c) => c.id));
      setIsPro(!!b.data.is_pro);
      setLoading(false);
    });
  }, []);

  const toggle = (id) => setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const submit = async () => {
    if (!isPro) { onUpgrade(); return; }
    if (!emailAddr || !emailAddr.includes('@')) { toast.error('Customer email required'); return; }
    if (!selectedIds.length) { toast.error('Pick at least one certificate'); return; }
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/suppliers/me/certificates/send`, {
        chat_id: chatId,
        source: 'chat',
        recipient_name: name || null,
        recipient_email: emailAddr,
        certificate_ids: selectedIds,
      });
      toast.success('Certificates sent');
      onSent(data.chat_message);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 403 && detail?.error === 'upgrade_required') {
        onUpgrade();
      } else {
        toast.error(detail?.message || (typeof detail === 'string' ? detail : null) || 'Could not send');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm sm:p-6"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="cert-send-chat-modal"
    >
      <div className="w-full sm:max-w-md bg-[#15161B] border border-white/10 sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-5 pt-5 pb-3 border-b border-white/5 flex items-start justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-wider font-black text-[#C29D52]/85/80">Send certificates</p>
            <h3 className="font-black text-lg mt-0.5">Instant trust delivery</h3>
            <p className="text-white/50 text-xs mt-0.5">Lands as a branded email + a chat card.</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white -mr-2 p-2">×</button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {loading ? (
            <p className="text-white/40 text-sm text-center py-6">Loading certificates…</p>
          ) : !certs.length ? (
            <div className="text-center py-6">
              <ShieldCheck size={28} weight="fill" className="mx-auto text-[#C29D52]/60 mb-2" />
              <p className="font-bold text-sm">No certificates on file yet</p>
              <p className="text-white/50 text-xs mt-1 mb-4">Upload PAT + PLI first.</p>
              <button
                onClick={onGoCompliance}
                className="py-2 px-4 rounded-lg text-xs font-bold text-white"
                style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
                data-testid="cert-send-go-compliance"
              >
                Go to Compliance Hub
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Recipient name"
                  className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#B89762]/40/40"
                  data-testid="cert-send-recipient-name"
                />
                <input
                  type="email"
                  value={emailAddr}
                  onChange={(e) => setEmailAddr(e.target.value)}
                  placeholder="Recipient email"
                  className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#B89762]/40/40"
                  data-testid="cert-send-recipient-email"
                />
              </div>
              <div>
                <label className="block text-[11px] font-black text-white/60 uppercase tracking-wider mb-1.5">Certificates</label>
                <div className="flex flex-col gap-1.5">
                  {certs.map((c) => (
                    <label
                      key={c.id}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.04] border border-white/10 text-sm cursor-pointer hover:bg-white/[0.07]"
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(c.id)}
                        onChange={() => toggle(c.id)}
                        data-testid={`cert-send-toggle-${c.type}`}
                      />
                      <span className="font-bold">
                        {c.type === 'pat' ? 'PAT Certificate' : 'Public Liability Insurance'}
                      </span>
                      {c.certificate_number && <span className="text-white/40 text-xs">· {c.certificate_number}</span>}
                    </label>
                  ))}
                </div>
              </div>

              {!isPro && (
                <div className="flex items-start gap-2 rounded-lg border border-[#B89762]/40/20 bg-[#B89762]/30/5 px-3 py-2.5 text-xs" data-testid="cert-send-upgrade-nudge">
                  <Lock size={14} weight="fill" className="text-[#C29D52] mt-0.5 shrink-0" />
                  <p>
                    <span className="font-bold">Unlock instant certificate sending.</span>
                    <span className="text-white/60"> Suppliers who send documents get booked faster.</span>
                  </p>
                </div>
              )}
            </>
          )}
        </div>
        {certs.length > 0 && (
          <div className="border-t border-white/5 px-5 py-3 flex gap-2">
            <button
              onClick={submit}
              disabled={busy}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
              data-testid="cert-send-submit-btn"
            >
              {isPro ? <FileText size={14} weight="fill" /> : <Lock size={14} weight="fill" />}
              {busy ? 'Sending…' : isPro ? 'Send instantly' : 'Upgrade to send'}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl text-sm font-bold bg-white/5 border border-white/10 hover:bg-white/10"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const QuoteCard = ({ message, isSupplier, onAccept, onDecline, eventContext }) => {
  const [busy, setBusy] = useState(null);
  const run = async (fn, label) => {
    setBusy(label);
    try { await fn(); } finally { setBusy(null); }
  };
  const q = message.quote || {};
  const status = q.status || 'pending';
  const priceLabel = formatPrice(q.price);
  const hoursLabel = q.duration_hours
    ? `${q.duration_hours === Math.floor(q.duration_hours) ? q.duration_hours : q.duration_hours.toFixed(1)} hours`
    : '';

  if (status === 'accepted') {
    return (
      <div className="flex justify-center" data-testid={`quote-${message.id}`}>
        <div className="rounded-2xl px-4 py-3 border border-emerald-400/30 bg-emerald-400/10 text-emerald-100 max-w-sm w-full text-center">
          <div className="flex items-center justify-center gap-2 font-bold">
            <CheckCircle size={16} weight="fill" />
            Booking confirmed 🎉
          </div>
          <p className="text-emerald-200/80 text-xs mt-1 font-bold">{priceLabel} · {hoursLabel}</p>
          {eventContext?.event_date && (
            <p className="text-emerald-200/70 text-[11px] mt-0.5">{formatBookingDate(eventContext.event_date)}</p>
          )}
        </div>
      </div>
    );
  }
  if (status === 'declined') {
    return (
      <div className="flex justify-center" data-testid={`quote-${message.id}`}>
        <div className="rounded-2xl px-4 py-2.5 border border-white/10 bg-white/5 text-white/50 flex items-center gap-2 text-sm">
          <XCircle size={16} weight="fill" /> Quote declined · {priceLabel}
        </div>
      </div>
    );
  }

  // pending
  return (
    <div className="flex justify-center" data-testid={`quote-${message.id}`}>
      <div
        className="rounded-2xl px-4 py-3 border text-sm w-full max-w-sm text-white"
        style={{
          borderColor: 'rgba(194,157,82,0.3)',
          background: 'rgba(194,157,82,0.05)',
        }}
      >
        <div className="flex items-center gap-2 mb-2 text-[#C29D52]/85">
          <CurrencyGbp size={14} weight="fill" />
          <p className="font-black text-[11px] uppercase tracking-wider">Quote</p>
        </div>
        <p className="text-2xl font-black text-white">{priceLabel}</p>
        {hoursLabel && <p className="text-xs text-white/60 mt-0.5">{hoursLabel}</p>}
        {eventContext?.event_date && (
          <p className="text-[11px] text-white/50 mt-0.5">{formatBookingDate(eventContext.event_date)}</p>
        )}
        {q.note && (
          <p className="text-xs text-white/60 italic mt-2 line-clamp-3">&ldquo;{q.note}&rdquo;</p>
        )}
        {!isSupplier ? (
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => run(onAccept, 'accept')}
              disabled={!!busy}
              className="flex-1 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
              data-testid="quote-accept-btn"
            >
              {busy === 'accept' ? '…' : 'Accept'}
            </button>
            <button
              onClick={() => run(onDecline, 'decline')}
              disabled={!!busy}
              className="flex-1 py-2 rounded-lg text-xs font-bold bg-white/5 hover:bg-white/10 border border-white/10 disabled:opacity-60"
              data-testid="quote-decline-btn"
            >
              {busy === 'decline' ? '…' : 'Decline'}
            </button>
          </div>
        ) : (
          <>
            <p className="text-white/50 text-[11px] italic text-center mt-3">
              Waiting for customer to respond…
            </p>
            {q.seen_at && (
              <p
                className="text-[#C29D52]/85/70 text-[10px] text-center mt-1"
                data-testid="quote-seen-tag"
              >
                Seen · {formatRelative(q.seen_at)}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const BookingCard = ({ booking, isSupplier, onAccept, onDecline, supplierName, eventContext }) => {
  const [busy, setBusy] = useState(null);
  const run = async (fn, label) => {
    setBusy(label);
    try { await fn(); } finally { setBusy(null); }
  };

  const base = 'rounded-2xl px-4 py-3 border text-sm';
  const isInstant = !!booking.is_instant;
  const wasAutoAccepted = !!booking.auto_accepted;

  if (booking.status === 'confirmed') {
    return (
      <div className="flex justify-center" data-testid={`booking-${booking.id}`}>
        <div className={`${base} border-emerald-400/30 bg-emerald-400/10 text-emerald-100 flex flex-col items-center text-center max-w-sm`}>
          <div className="flex items-center gap-2 font-bold">
            <CheckCircle size={16} weight="fill" />
            {wasAutoAccepted ? 'Booking confirmed instantly 🎉' : 'Booking Confirmed'}
          </div>
          <p className="text-emerald-200/70 text-xs mt-1">
            {wasAutoAccepted
              ? `You're locked in for ${formatBookingDate(booking.event_date)}`
              : 'This event is now locked in'}
          </p>
          {isInstant && booking.event_date && !wasAutoAccepted && (
            <p className="text-emerald-200/80 text-xs mt-0.5 font-bold">
              {formatBookingDate(booking.event_date)}{booking.duration_hours ? ` · ${booking.duration_hours} hours` : ''}
            </p>
          )}
          {isSupplier && (
            <p className="text-emerald-200/60 text-[10px] mt-1.5 italic" data-testid="booking-ranking-hint">
              {isInstant
                ? '⚡ Instant bookings like this improve your visibility'
                : '🏆 Bookings like this improve your visibility'}
            </p>
          )}
        </div>
      </div>
    );
  }
  if (booking.status === 'declined') {
    return (
      <div className="flex justify-center" data-testid={`booking-${booking.id}`}>
        <div className={`${base} border-white/10 bg-white/5 text-white/50 flex items-center gap-2`}>
          <XCircle size={16} weight="fill" /> Booking declined
        </div>
      </div>
    );
  }

  // pending
  return (
    <div className="flex justify-center" data-testid={`booking-${booking.id}`}>
      <div
        className={`${base} w-full max-w-sm text-white`}
        style={{
          borderColor: isInstant ? 'rgba(184,151,98,0.4)' : 'rgba(194,157,82,0.3)',
          background: isInstant
            ? 'linear-gradient(180deg, rgba(184,151,98,0.15), rgba(194,157,82,0.05))'
            : 'rgba(194,157,82,0.05)',
        }}
      >
        <div className="flex items-center gap-2 mb-2 text-[#C29D52]/85">
          {isInstant ? <Lightning size={14} weight="fill" /> : <Calendar size={14} weight="fill" />}
          <p className="font-black text-[11px] uppercase tracking-wider">
            {isInstant ? '⚡ Instant booking request' : 'Booking Request'}
          </p>
        </div>
        {isInstant && booking.event_date ? (
          <div className="space-y-0.5 mb-3 text-white/85">
            {isSupplier && eventContext && (
              <p className="text-[10px] font-black uppercase tracking-wider text-[#C29D52]/85 mb-1">
                {eventContext.event_name || 'Event'} {eventContext.event_type_emoji}
              </p>
            )}
            <p className="font-bold text-sm">{formatBookingDate(booking.event_date)}</p>
            <p className="text-xs text-white/60">
              {booking.location ? `${booking.location} · ` : ''}
              {booking.duration_hours ? `${booking.duration_hours} hours` : ''}
            </p>
            {isSupplier && eventContext?.event_type_label && (
              <p className="text-xs text-white/55 pt-0.5">
                {eventContext.event_type_label} {eventContext.event_type_emoji}
                {eventContext.guest_count ? ` · ${eventContext.guest_count} guests` : ''}
              </p>
            )}
            {isSupplier && (
              <p className="text-emerald-300 text-[11px] font-bold pt-1.5">
                ✓ This date is available
              </p>
            )}
            {isSupplier && (
              <p className="text-[#C29D52]/85/80 text-[10px] italic">
                High-intent enquiry — ready to book
              </p>
            )}
          </div>
        ) : (
          <p className="text-white/80 mb-3 text-sm">
            {isSupplier
              ? 'Waiting for customer to confirm…'
              : `${supplierName} wants to confirm your event.`}
          </p>
        )}
        {isInstant && booking.note && !isSupplier && (
          <div className="text-xs text-white/50 italic mb-3 line-clamp-3">&ldquo;{booking.note}&rdquo;</div>
        )}
        {isInstant && booking.note && isSupplier && (
          <div className="text-xs text-white/60 italic mb-3 line-clamp-3">&ldquo;{booking.note}&rdquo;</div>
        )}

        {/* Supplier side — instant bookings: accept/decline. Customer side only shows accept for non-instant */}
        {(isSupplier && isInstant) && (
          <div className="flex gap-2">
            <button
              onClick={() => run(onAccept, 'accept')}
              disabled={!!busy}
              className="flex-1 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
              data-testid="accept-booking-btn"
            >
              {busy === 'accept' ? '…' : 'Accept booking'}
            </button>
            <button
              onClick={() => run(onDecline, 'decline')}
              disabled={!!busy}
              className="flex-1 py-2 rounded-lg text-xs font-bold bg-white/5 hover:bg-white/10 border border-white/10 disabled:opacity-60"
              data-testid="decline-booking-btn"
            >
              {busy === 'decline' ? '…' : 'Decline'}
            </button>
          </div>
        )}
        {!isSupplier && !isInstant && (
          <div className="flex gap-2">
            <button
              onClick={() => run(onAccept, 'accept')}
              disabled={!!busy}
              className="flex-1 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
              data-testid="accept-booking-btn"
            >
              {busy === 'accept' ? '…' : 'Accept Booking'}
            </button>
            <button
              onClick={() => run(onDecline, 'decline')}
              disabled={!!busy}
              className="flex-1 py-2 rounded-lg text-xs font-bold bg-white/5 hover:bg-white/10 border border-white/10 disabled:opacity-60"
              data-testid="decline-booking-btn"
            >
              {busy === 'decline' ? '…' : 'Decline'}
            </button>
          </div>
        )}
        {!isSupplier && isInstant && (
          <p className="text-white/50 text-[11px] italic text-center">
            Waiting for supplier to respond…
          </p>
        )}
      </div>
    </div>
  );
};

function formatBookingDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

function formatPrice(price) {
  if (price === null || price === undefined) return '£0';
  const n = Number(price);
  if (Number.isNaN(n)) return `£${price}`;
  if (n === Math.floor(n)) return `£${n.toLocaleString('en-GB')}`;
  return `£${n.toFixed(2)}`;
}

function formatRelative(iso) {
  if (!iso) return '';
  try {
    const then = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - then);
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  } catch {
    return '';
  }
}

export default ChatPage;
